import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ShoppingBasket, Loader2, Download, Refrigerator } from "lucide-react";
import { SubscribeGate } from "../components/grocery/SubscribeGate";
import { GroceryListBuilder } from "../components/grocery/GroceryListBuilder";
import { GroceryStorePicker } from "../components/grocery/GroceryStorePicker";
import { GroceryResults } from "../components/grocery/GroceryResults";
import { VoiceCapture } from "../components/grocery/VoiceCapture";
import { KitchenTray } from "../components/grocery/KitchenTray";
import { flushVoice } from "../lib/voiceQueue";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/shopnsave`;
const LS_EMAIL = "sns_email";
const LS_LIST = "sns_list";
const LS_STORES = "sns_stores";
const LS_THRESHOLD = "sns_threshold";
const LS_TOKEN = "sns_token";
const LS_BUDGET = "sns_budget";

const readLS = (k, fb) => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : fb; } catch { return fb; } };
const writeLS = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* noop */ } };
const newKey = () => (crypto?.randomUUID?.() || String(Date.now()) + Math.random().toString(36).slice(2));

// Swap the document manifest + theme while this route is mounted, so installing
// here creates a SEPARATE Shop'N'Save home-screen icon that opens straight to the
// PWA (scoped to /shopnsave/pwa) — not the main Misteck app.
function useShopNSaveManifest() {
  useEffect(() => {
    const head = document.head;
    const link = head.querySelector('link[rel="manifest"]');
    const prevHref = link ? link.getAttribute("href") : null;
    if (link) link.setAttribute("href", "/shopnsave-manifest.json");

    const theme = head.querySelector('meta[name="theme-color"]');
    const prevTheme = theme ? theme.getAttribute("content") : null;
    if (theme) theme.setAttribute("content", "#2E7D4F");

    const appleTitle = head.querySelector('meta[name="apple-mobile-web-app-title"]');
    const prevAppleTitle = appleTitle ? appleTitle.getAttribute("content") : null;
    if (appleTitle) appleTitle.setAttribute("content", "Misteck Shop'N'Save");

    const appleIcon = head.querySelector('link[rel="apple-touch-icon"]');
    const prevAppleIcon = appleIcon ? appleIcon.getAttribute("href") : null;
    if (appleIcon) appleIcon.setAttribute("href", "/shopnsave-icon-192.png");

    return () => {
      if (link && prevHref) link.setAttribute("href", prevHref);
      if (theme && prevTheme) theme.setAttribute("content", prevTheme);
      if (appleTitle && prevAppleTitle) appleTitle.setAttribute("content", prevAppleTitle);
      if (appleIcon && prevAppleIcon) appleIcon.setAttribute("href", prevAppleIcon);
    };
  }, []);
}

// Capture the deferred install prompt so we can show a first-party "Install" button.
function useInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(
    () => window.matchMedia("(display-mode: standalone)").matches ||
          window.navigator.standalone === true
  );
  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);
  const install = useCallback(async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* dismissed */ }
    setDeferred(null);
  }, [deferred]);
  return { canInstall: !!deferred && !installed, install };
}

export default function ShopNSavePWA() {
  useShopNSaveManifest();
  const { canInstall, install } = useInstallPrompt();
  const [booting, setBooting] = useState(true);
  const [access, setAccess] = useState({ active: false, plan: "none", days_left: 0 });
  const [email, setEmail] = useState(() => readLS(LS_EMAIL, ""));
  const [gateOpen, setGateOpen] = useState(false);
  const [gateMode, setGateMode] = useState("trial");
  const [subBusy, setSubBusy] = useState(false);
  const [nearest, setNearest] = useState(null);
  const [locating, setLocating] = useState(false);
  const [located, setLocated] = useState(false);

  const [view, setView] = useState("build");
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [items, setItems] = useState(() => readLS(LS_LIST, []));
  const [selectedStores, setSelectedStores] = useState(() => readLS(LS_STORES, []));
  const [threshold, setThreshold] = useState(() => readLS(LS_THRESHOLD, 5));
  const [budget, setBudget] = useState(() => readLS(LS_BUDGET, ""));
  const [seeding, setSeeding] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [result, setResult] = useState(null);
  const [kitchenOpen, setKitchenOpen] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        fetch(`${API}/products`).then((r) => r.json()),
        fetch(`${API}/stores`).then((r) => r.json()),
      ]);
      setProducts(p.products || []);
      setStores(s.stores || []);
      setSelectedStores((cur) => (cur.length ? cur : (s.stores || []).map((x) => x.id)));
    } catch { /* offline — keep drafts */ }
  }, []);

  // boot: handle Stripe return / dev unlock / saved session, then load catalog
  useEffect(() => {
    (async () => {
      const q = new URLSearchParams(window.location.search);
      const cleanUrl = () => window.history.replaceState({}, "", "/shopnsave/pwa");
      try {
        if (q.get("sub_cancel")) {
          toast("Checkout canceled — you can try again anytime.");
          cleanUrl();
        } else if (q.get("dev_unlock")) {
          const em = q.get("dev_unlock").toLowerCase();
          const d = await fetch(`${API}/dev-trial`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: em }),
          }).then((r) => r.json());
          if (d.active) {
            setAccess({ active: true, plan: "premium", days_left: null });
            setEmail(em); writeLS(LS_EMAIL, em);
            if (d.token) writeLS(LS_TOKEN, d.token);
          }
          cleanUrl();
        } else if (q.get("sub_session")) {
          const d = await fetch(`${API}/session-status/${q.get("sub_session")}`).then((r) => r.json());
          if (d.active) {
            setAccess({ active: true, plan: "premium", days_left: null });
            if (d.email) { setEmail(d.email); writeLS(LS_EMAIL, d.email); }
            if (d.token) writeLS(LS_TOKEN, d.token);
            toast.success("You're in! Enjoy Shop'N'Save 🎉");
          }
          cleanUrl();
        } else {
          const savedTok = readLS(LS_TOKEN, "");
          if (savedTok) {
            const d = await fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${savedTok}` } })
              .then((r) => (r.ok ? r.json() : null)).catch(() => null);
            if (d) setAccess({ active: !!d.active, plan: d.plan || "none", days_left: d.days_left });
            else { try { localStorage.removeItem(LS_TOKEN); } catch { /* noop */ } }
          }
        }
      } catch { /* ignore */ }
      await loadData();
      setBooting(false);
    })();
  }, [loadData]);

  useEffect(() => writeLS(LS_LIST, items), [items]);
  useEffect(() => writeLS(LS_STORES, selectedStores), [selectedStores]);
  useEffect(() => writeLS(LS_THRESHOLD, threshold), [threshold]);
  useEffect(() => writeLS(LS_BUDGET, budget), [budget]);

  const budgetCents = budget && Number(budget) > 0 ? Math.round(Number(budget) * 100) : null;

  const addItem = (it) => setItems((cur) => [...cur, { key: newKey(), ...it }]);
  const removeItem = (key) => setItems((cur) => cur.filter((i) => i.key !== key));
  const toggleStore = (id) =>
    setSelectedStores((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const seedDemo = async () => {
    setSeeding(true);
    try {
      await fetch(`${API}/seed-demo`, { method: "POST" });
      await loadData();
      toast.success("Demo specials loaded — add them to your list!");
    } catch { toast.error("Couldn't load demo specials."); }
    setSeeding(false);
  };

  const doOptimize = async (em) => {
    const pids = items.filter((i) => i.product_id).map((i) => i.product_id);
    if (pids.length === 0) { toast.error("Pick some tracked items from the suggestions first."); return; }
    setOptimizing(true);
    try {
      const tok = readLS(LS_TOKEN, "");
      const res = await fetch(`${API}/optimize`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(tok ? { Authorization: `Bearer ${tok}` } : {}) },
        body: JSON.stringify({ product_ids: pids, store_ids: selectedStores, convenience_threshold_dollars: threshold }),
      });
      if (res.status === 401 || res.status === 402) {
        setAccess((a) => ({ ...a, active: false })); setGateMode("trial"); setGateOpen(true); return;
      }
      const d = await res.json();
      if (!res.ok) { toast.error(d.detail || "Could not calculate your split."); return; }
      setResult(d); setView("results");
    } catch { toast.error("Could not calculate your split."); }
    finally { setOptimizing(false); }
  };

  const runOptimize = () => {
    if (!access.active) { setGateMode("trial"); setGateOpen(true); return; }
    doOptimize(email);
  };

  const startTrial = async (em, password) => {
    setSubBusy(true);
    try {
      let res = await fetch(`${API}/auth/register`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: em, password }),
      });
      if (res.status === 409) {
        res = await fetch(`${API}/auth/login`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: em, password }),
        });
      }
      const d = await res.json();
      if (!res.ok) { toast.error(d.detail || "Couldn't sign you in."); return; }
      setAccess({ active: d.active, plan: d.plan, days_left: d.days_left });
      setEmail(em); writeLS(LS_EMAIL, em); if (d.token) writeLS(LS_TOKEN, d.token);
      setGateOpen(false);
      if (d.active) {
        toast.success(d.plan === "founder" ? "Welcome, founder — free forever 🎉" : "You're in! Free month started 🎉");
        if (view === "stores") doOptimize(em);
      } else if (d.plan === "expired") {
        setGateMode("expired");
      }
    } catch { toast.error("Something went wrong. Try again."); }
    setSubBusy(false);
  };

  const subscribe = async (em) => {
    setSubBusy(true);
    try {
      const d = await fetch(`${API}/create-checkout-session`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: em, origin_url: window.location.origin }),
      }).then((r) => r.json());
      if (d.checkout_url) { writeLS(LS_EMAIL, em); window.location.href = d.checkout_url; }
      else toast.error(d.detail || "Could not start checkout.");
    } catch { toast.error("Could not start checkout. Try again."); }
    setSubBusy(false);
  };

  const onUseLocation = () => {
    if (!navigator.geolocation) { toast.error("Location isn't available on this device."); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const d = await fetch(`${API}/nearest`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
          }).then((r) => r.json());
          setNearest(d.nearest || {}); setLocated(true);
          toast.success("Found the stores closest to you.");
        } catch { toast.error("Couldn't load nearby stores."); }
        setLocating(false);
      },
      () => { toast.error("Location permission denied."); setLocating(false); },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const restart = () => { setResult(null); setItems([]); setView("build"); };

  const emailNow = async () => {
    const pids = items.filter((i) => i.product_id).map((i) => i.product_id);
    if (pids.length === 0) { toast.error("Add some tracked items first."); return; }
    const tok = readLS(LS_TOKEN, "");
    try {
      const res = await fetch(`${API}/email-now`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(tok ? { Authorization: `Bearer ${tok}` } : {}) },
        body: JSON.stringify({
          product_ids: pids, store_ids: selectedStores,
          convenience_threshold_dollars: threshold, weekly_budget_cents: budgetCents,
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.status === 429) { toast.error("You've sent a few already — try again in a bit."); return; }
      if (res.status === 401 || res.status === 402) {
        setAccess((a) => ({ ...a, active: false })); setGateMode("trial"); setGateOpen(true); return;
      }
      if (!res.ok) { toast.error(d.detail || "Couldn't send your list."); return; }
      toast.success(`Sent to ${d.sent_to || email} ✓`);
    } catch { toast.error("Couldn't send your list. Try again."); }
  };

  // Apply a transcribed voice note: additions go to the list, depletions were
  // already marked empty in the pantry server-side.
  const handleVoiceResult = useCallback((d) => {
    const its = (d && d.items) || [];
    const added = its.filter((i) => i.action === "add");
    const depleted = its.filter((i) => i.action === "deplete");
    added.forEach((i) => addItem({ name: i.name, product_id: i.global_product_id, aisle: i.aisle }));
    if (added.length) toast.success(`Added ${added.map((i) => i.name).join(", ")}`);
    if (depleted.length) toast(`🧺 Marked empty: ${depleted.map((i) => i.name).join(", ")}`);
    if (!added.length && !depleted.length && d?.transcript) {
      toast(`Heard: "${d.transcript}" — couldn't match an item.`);
    }
  }, []);

  const addFromPantry = (item) =>
    addItem({ name: item.name, product_id: item.global_product_id, aisle: item.aisle });

  // Flush any notes recorded offline — on load and whenever we regain the network.
  useEffect(() => {
    const tok = readLS(LS_TOKEN, "");
    if (!tok) return;
    const run = () => flushVoice(API, tok, handleVoiceResult);
    run();
    window.addEventListener("online", run);
    return () => window.removeEventListener("online", run);
  }, [access.active, handleVoiceResult]);

  return (
    <div className="h-full w-full overflow-y-auto overflow-x-hidden bg-[#FBF7EF] text-[#2B2A26]" data-testid="sns-page">
      {/* top bar */}
      <header className="sticky top-0 z-20 bg-[#FBF7EF]/90 backdrop-blur border-b border-black/5">
        <div className="max-w-md mx-auto px-5 py-3.5 flex items-center justify-between">
          <span className="flex items-center gap-2">
            <span className="h-8 w-8 grid place-items-center rounded-xl bg-[#2E7D4F] text-white">
              <ShoppingBasket className="h-4.5 w-4.5" />
            </span>
            <span className="leading-none">
              <span className="block text-[9px] font-bold tracking-[0.22em] text-[#2E7D4F]/70 uppercase">Misteck</span>
              <span className="block font-black text-lg tracking-tight">
                Shop<span className="text-[#E4572E]">'N'</span>Save
              </span>
            </span>
          </span>
          <span className="flex items-center gap-2">
            {access.active && (
              <button
                onClick={() => setKitchenOpen(true)}
                data-testid="sns-kitchen-open"
                className="flex items-center gap-1.5 text-[12px] font-bold text-[#2E7D4F] bg-[#2E7D4F]/10 rounded-full px-3 py-1.5 active:scale-95 transition-transform"
              >
                <Refrigerator className="h-3.5 w-3.5" /> My Kitchen
              </button>
            )}
            {access.plan === "founder" && (
              <span data-testid="sns-plan-badge" className="text-[11px] font-bold text-[#8a6d1a] bg-[#f3e4b0] rounded-full px-3 py-1">Founder · Free</span>
            )}
            {access.plan === "premium" && (
              <span data-testid="sns-plan-badge" className="text-[11px] font-semibold text-[#2E7D4F] bg-[#2E7D4F]/10 rounded-full px-3 py-1">Premium</span>
            )}
            {access.plan === "trial" && access.days_left != null && (
              <span data-testid="sns-plan-badge" className="text-[11px] font-semibold text-[#E4572E] bg-[#E4572E]/10 rounded-full px-3 py-1">{access.days_left}d left</span>
            )}
            {canInstall && (
              <button
                onClick={install}
                data-testid="sns-install-btn"
                className="flex items-center gap-1.5 text-[12px] font-bold text-white bg-[#2E7D4F] rounded-full px-3 py-1.5 active:scale-95 transition-transform"
              >
                <Download className="h-3.5 w-3.5" /> Install
              </button>
            )}
          </span>
        </div>
      </header>

      {access.plan === "trial" && access.days_left != null && (
        <div className="bg-[#2E7D4F] text-white text-center text-[12px] font-semibold py-1.5" data-testid="sns-trial-banner">
          🎁 {access.days_left} day{access.days_left === 1 ? "" : "s"} left in your free month
        </div>
      )}

      <main className="max-w-md mx-auto px-5 py-5 pb-24">
        {booting ? (
          <div className="py-24 grid place-items-center text-black/30">
            <Loader2 className="h-7 w-7 animate-spin" />
          </div>
        ) : view === "build" ? (
          <GroceryListBuilder
            items={items}
            products={products}
            onAdd={addItem}
            onRemove={removeItem}
            onSeedDemo={seedDemo}
            seeding={seeding}
            onNext={() => setView("stores")}
            budget={budget}
            onBudget={setBudget}
            voiceSlot={
              <VoiceCapture
                apiBase={API}
                token={readLS(LS_TOKEN, "")}
                active={access.active}
                onResult={handleVoiceResult}
                onNeedAccess={() => { setGateMode("trial"); setGateOpen(true); }}
              />
            }
          />
        ) : view === "stores" ? (
          <GroceryStorePicker
            stores={stores}
            selected={selectedStores}
            onToggle={toggleStore}
            threshold={threshold}
            onThreshold={setThreshold}
            onBack={() => setView("build")}
            onOptimize={runOptimize}
            optimizing={optimizing}
            nearest={nearest}
            onUseLocation={onUseLocation}
            locating={locating}
            located={located}
          />
        ) : (
          <GroceryResults result={result} onRestart={restart} budgetCents={budgetCents} onEmailNow={emailNow} />
        )}
      </main>

      {gateOpen && (
        <SubscribeGate
          mode={gateMode}
          onStartTrial={startTrial}
          onSubscribe={subscribe}
          busy={subBusy}
          onClose={() => setGateOpen(false)}
        />
      )}

      <KitchenTray
        open={kitchenOpen}
        onClose={() => setKitchenOpen(false)}
        apiBase={API}
        token={readLS(LS_TOKEN, "")}
        onAddToList={addFromPantry}
        scheduleCtx={{
          product_ids: items.filter((i) => i.product_id).map((i) => i.product_id),
          store_ids: selectedStores,
          threshold,
          weekly_budget_cents: budgetCents,
        }}
      />
    </div>
  );
}
